import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowRight,
  Briefcase,
  Building2,
  Calendar,
  ChevronRight,
  Euro,
  Layers,
  MapPin,
  Send,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import SEO from "@/components/SEO";
import ApplicationFormModal from "@/components/careers/ApplicationFormModal";
import { Button } from "@/components/ui/button";
import { useApplicationForm } from "@/hooks/useApplicationForm";
import apiClient, { getApiErrorMessage } from "@/lib/api";
import { getOfferContractTypes } from "@/lib/jobOffers";

const markdownComponents = {
  h1: ({ children }) => <h1 className="text-2xl font-heading font-bold text-white mt-8 mb-4">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-heading font-semibold text-white mt-6 mb-3">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-heading font-semibold text-white mt-5 mb-2">{children}</h3>,
  p: ({ children }) => <p className="text-slate-300 leading-relaxed mb-4">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-inside space-y-2 mb-4 text-slate-300">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside space-y-2 mb-4 text-slate-300">{children}</ol>,
  li: ({ children }) => <li className="text-slate-300 ml-2">{children}</li>,
  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
  em: ({ children }) => <em className="text-slate-200 italic">{children}</em>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-lenvia-blue pl-4 my-4 text-slate-400 italic">{children}</blockquote>,
  code: ({ children }) => <code className="bg-white/10 px-2 py-1 rounded text-sm text-lenvia-blue font-mono">{children}</code>,
  hr: () => <hr className="my-6 border-white/10" />,
};

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);

  const {
    applicationData,
    cvFile,
    isSubmitting,
    handleApplicationChange,
    handleFileChange,
    clearCvFile,
    resetApplicationForm,
    submitApplication,
  } = useApplicationForm();

  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.get(`/jobs/${jobId}`);
        setJob(response.data);
      } catch (fetchError) {
        setError(getApiErrorMessage(fetchError, "Offre non trouvée"));
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      fetchJob();
    }
  }, [jobId]);

  const closeApplicationForm = () => {
    setShowApplicationForm(false);
    resetApplicationForm("cv-upload-detail");
  };

  const handleApplicationSubmit = async (event) => {
    event.preventDefault();

    if (!job) {
      toast.error("Offre non disponible.");
      return;
    }

    const sent = await submitApplication({ selectedJob: job });
    if (sent) {
      closeApplicationForm();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-lenvia-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-lenvia-blue/30 border-t-lenvia-blue rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Chargement de l'offre...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-lenvia-bg flex items-center justify-center">
        <div className="text-center glass-card p-8">
          <Briefcase className="w-16 h-16 text-slate-500 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-semibold text-white mb-2">Offre non trouvée</h2>
          <p className="text-slate-400 mb-6">Cette offre n'existe pas ou a été supprimée.</p>
          <Button className="btn-primary" onClick={() => navigate("/carrieres")}>
            Voir toutes les offres
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  const contractTypes = getOfferContractTypes(job);

  return (
    <>
      <SEO
        title={`Offre ${job.title} | Lenvia Carrières`}
        description={`Découvrez le détail de l'offre ${job.title} et postulez en ligne chez Lenvia.`}
      />

      <div className="pt-28 pb-20">
        <div className="lenvia-container">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-sm text-slate-500 mb-6"
              >
                <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
                <ChevronRight className="w-4 h-4" />
                <Link to="/carrieres" className="hover:text-white transition-colors">Carrières</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-slate-300">{job.title}</span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="glass-card p-8 mb-8"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                  <div className="flex-grow">
                    <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">{job.title}</h1>

                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      {contractTypes.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                          <Briefcase className="w-4 h-4 text-lenvia-blue" />
                          {contractTypes.map((contractType) => (
                            <span
                              key={`${job.id}-${contractType}`}
                              className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-slate-300"
                            >
                              {contractType}
                            </span>
                          ))}
                        </div>
                      )}
                      {job.location && (
                        <span className="flex items-center gap-2 text-slate-300 bg-white/5 px-3 py-1.5 rounded-full">
                          <MapPin className="w-4 h-4 text-lenvia-blue" />
                          {job.location}
                        </span>
                      )}
                      {job.department && (
                        <span className="flex items-center gap-2 text-lenvia-violet bg-lenvia-violet/20 px-3 py-1.5 rounded-full border border-lenvia-violet/30">
                          <Building2 className="w-4 h-4" />
                          {job.department}
                        </span>
                      )}
                    </div>

                    {job.salary_range && (
                      <div className="flex items-center gap-2 text-slate-400">
                        <Euro className="w-4 h-4" />
                        <span>{job.salary_range}</span>
                      </div>
                    )}

                    {job.publication_date && (
                      <div className="flex items-center gap-2 text-slate-500 mt-2 text-sm">
                        <Calendar className="w-4 h-4" />
                        <span>Publié le {new Date(job.publication_date).toLocaleDateString("fr-FR")}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex-shrink-0">
                    <Button className="btn-primary text-base w-full md:w-auto" onClick={() => setShowApplicationForm(true)}>
                      Postuler maintenant
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="glass-card p-8 mb-8"
              >
                <h2 className="font-heading text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-lenvia-blue" />
                  Description du poste
                </h2>

                {job.description && <p className="text-slate-300 leading-relaxed mb-6">{job.description}</p>}

                {job.detailed_description ? (
                  <div className="prose-lenvia">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {job.detailed_description}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-slate-400 italic">Aucune description détaillée disponible.</p>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="glass-card p-8 text-center"
              >
                <h3 className="font-heading text-xl font-semibold text-white mb-2">Intéressé(e) par ce poste ?</h3>
                <p className="text-slate-400 mb-6">Envoyez-nous votre candidature et rejoignez l'équipe Lenvia.</p>
                <Button className="btn-primary text-base" onClick={() => setShowApplicationForm(true)}>
                  Postuler à cette offre
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </motion.div>
            </div>
        </div>
      </div>

      <ApplicationFormModal
        open={showApplicationForm}
        onClose={closeApplicationForm}
        selectedJob={job}
        applicationData={applicationData}
        cvFile={cvFile}
        isSubmitting={isSubmitting}
        onApplicationChange={handleApplicationChange}
        onFileChange={handleFileChange}
        onClearFile={clearCvFile}
        onSubmit={handleApplicationSubmit}
        fileInputId="cv-upload-detail"
      />
    </>
  );
};

export default JobDetailPage;
